import { comparer } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { FormGroup } from "react-bootstrap";
import { useFormContext } from "./Form";
import { useTranslation } from "./hooks/useTranslation";
import { SafeHTMLDisplay } from "./SafeHTMLDisplay";
export const EMPTY_VALUE = "-";
const hasValue = (value) => {
    return value.length;
};
export const StaticInput = observer(({ name, label, type = "text", formatter, dataId, dataTestId }) => {
    const { values } = useFormContext();
    const inputValue = type === "html" && values.get(name) ? (React.createElement(SafeHTMLDisplay, { html: values.get(name) })) : (React.createElement("span", { className: "form-control-static-value" }, formatter ? formatter(values.get(name)) : values.get(name)));
    return (React.createElement(RawStaticInput, { label: label, value: inputValue, type: type, dataId: dataId, dataTestId: dataTestId }));
});
export const RawStaticInput = ({ label, value, type = "text", labelHidden = false, dataId, dataTestId, }) => {
    return (React.createElement("div", { className: "form-control-static", "data-id": dataId, "data-testid": dataTestId },
        !labelHidden && (React.createElement("span", { className: "form-control-static-label" }, label)),
        type === "html" ? (React.createElement(HTMLValueWrapper, null, value)) : (React.createElement("span", { className: "form-control-static-value", style: { all: "unset", wordBreak: "break-word" } }, value))));
};
export const StaticInputValue = ({ label, staticFormatter, name, type = "text", labelHidden, dataId, dataTestId, dataLabel, }) => {
    const { values } = useFormContext();
    const value = values.get(name);
    const formatValue = () => {
        return staticFormatter ? staticFormatter(value) : value;
    };
    return (React.createElement(FormGroup, { "data-id": dataId && `static-${dataId}`, "data-testid": dataTestId && `static-${dataTestId}`, "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: value ? formatValue() : EMPTY_VALUE, type: type, labelHidden: labelHidden })));
};
export const StaticCheckbox = ({ label, staticFormatter, name, dataId, dataTestId, dataLabel }) => {
    const t = useTranslation();
    const { values } = useFormContext();
    const value = values.get(name);
    const isChecked = value === true || value === "true";
    const formatValue = () => {
        return staticFormatter ? staticFormatter(value) : t("Yes");
    };
    return (React.createElement(FormGroup, { "data-id": dataId && `static-${dataId}`, "data-testid": dataTestId && `static-${dataTestId}`, "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: isChecked ? formatValue() : EMPTY_VALUE })));
};
export const StaticTypeahead = ({ label, staticFormatter, name, multiple, dataId, dataTestId, dataLabel, }) => {
    const { values } = useFormContext();
    const value = values.get(name);
    const formatValue = () => {
        if (!value || (multiple && value && value.length === 0)) {
            return "-";
        }
        if (hasValue(value)) {
            return value.map((option, i) => {
                if (!option.label && !staticFormatter) {
                    return undefined;
                }
                return listValues(option, i, staticFormatter);
            });
        }
        if (value && !hasValue(value)) {
            if (!value.label && !staticFormatter) {
                return undefined;
            }
            return getDisplayValueForListValues(value, staticFormatter);
        }
    };
    return (React.createElement(FormGroup, { "data-id": dataId && `static-${dataId}`, "data-testid": dataTestId && `static-${dataTestId}`, "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: values ? formatValue() : EMPTY_VALUE })));
};
export const StaticMultipleValues = ({ label, multiple, isLoading, name, options, staticFormatter, labelHidden, dataId, dataTestId, dataLabel, className, }) => {
    const { values } = useFormContext();
    const value = values.get(name);
    const formatValue = () => {
        if (multiple) {
            const values = value.map((v) => options.find((o) => comparer.structural(o.value, v)));
            if (values.length === 0)
                return EMPTY_VALUE;
            return values.map((value, i) => {
                return listValues(value, i);
            });
        }
        const option = options.find((o) => comparer.structural(o.value, value));
        if (staticFormatter) {
            return staticFormatter(option);
        }
        return option ? option.label : value;
    };
    return (React.createElement(FormGroup, { "data-id": dataId && `static-${dataId}`, "data-testid": dataTestId && `static-${dataTestId}`, "data-label": dataLabel, className: className },
        React.createElement(RawStaticInput, { label: label, value: !isLoading && value !== undefined && value !== null
                ? formatValue()
                : EMPTY_VALUE, labelHidden: labelHidden })));
};
const getDisplayValueForListValues = (value, staticFormatter) => {
    if (staticFormatter) {
        return staticFormatter(value);
    }
    if (!staticFormatter && value?.label != null) {
        return value.label;
    }
    return EMPTY_VALUE;
};
const listValues = (option, i, staticFormatter) => {
    return `${i !== 0 ? ", " : ""}${getDisplayValueForListValues(option, staticFormatter)}`;
};
const HTMLValueWrapper = ({ children }) => {
    return React.createElement("div", { className: "form-control-static-html" }, children);
};
